import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import useTick from '../../hooks/utility/useTick';
import OrderLineItemsList from '../../components/alert-screens/shared/order-line-item/OrderLineItemsList';
import ReactDOM from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OrderSummary from '../../components/alert-screens/shared/OrderSummary';
import OrderPaymentBreakdown from '../../components/alert-screens/shared/OrderPaymentBreakdown';
import OrderMethodIcon from '../../components/order/OrderMethodIcon';
import TableBox, { TableBoxVariant } from '../../components/order/TableBox';
import useTableLabel from '../../hooks/useTableLabel';
import { ReceiptType } from '../../providers/PrintProvider';
import usePrinter from '../../hooks/usePrinter';
import OrderFulfillmentsManager from '../../services/api/OrderFulfillmentsManager';
import { toast } from 'react-toastify';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import useSound from '../../hooks/utility/useSound';
import { ClockCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { useHelpers } from '../../providers/HelpersProvider';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import { OrderFulfillmentStatus } from '../../services/exports/Constants';

const TimeLabel = ({ children, className }) => (
  <div
    className={classnames(
      'border border-orange-500/30 bg-orange-500/10 text-orange-800 flex flex-col justify-center items-center py-sm rounded-lg',
      className,
    )}
  >
    {children}
  </div>
);

const OrderPreparationReminderScreen = ({ order }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Alerts:OrderPreparationReminderScreen' });

  const { company } = useSelector((state) => state.company);
  const { playSound } = useSound();
  const { formatTime } = useHelpers();
  const { replaceOrderData } = useOngoingOrders();
  const { print, printing } = usePrinter();
  const tableLabel = useTableLabel();
  const { value: pickupEstimate } = useTick(() =>
    Math.floor(DateTime.fromISO(order.estimated_pickup_time).diff(DateTime.local(), 'minutes').as('minutes')),
  );
  const [loading, setLoading] = useState(false);

  const isExternalDelivery = OrderHelpers.isExternalDelivery(order);
  const shouldPrint = OrderHelpers.isPrintDeferredToDriverAssignment(order, company);

  useEffect(() => {
    playSound();
    const id = setInterval(() => playSound(), 10000);

    return () => clearInterval(id);
  }, []);

  const onConfirm = async () => {
    setLoading(true);
    const { success, data } = await OrderFulfillmentsManager.update(order.fulfillment_id, {
      is_driver_assignment_handled: isExternalDelivery,
      status: OrderFulfillmentStatus.Preparing,
    });

    if (!success) {
      toast.error(t('toasts.something_went_wrong'));

      return setLoading(false);
    }

    replaceOrderData(data.data);
    shouldPrint && print({ type: ReceiptType.Order, payload: data.data });
    setLoading(false);
  };

  return ReactDOM.createPortal(
    <RemoveScroll enabled className="w-full inset-0 fixed z-[1200] flex justify-center bg-orange-600 lg:p-md p-xs">
      <div className="max-h-full bg-white flex flex-col justify-center space-y-md w-full lg:w-3/5 rounded-lg m-auto p-md">
        <OrderMethodIcon order={order} size="xl" className="mx-auto" />
        <div className="flex-1 overflow-y-auto space-y-sm">
          <h4 className="mx-auto uppercase font-bold my-auto text-orange-600 text-center">
            {t(`titles.${isExternalDelivery ? 'external_delivery' : 'default'}`)}
          </h4>
          {OrderHelpers.isOnSite(order) && <TableBox variant={TableBoxVariant.Alert} label={tableLabel(order)} />}
          <TimeLabel>
            <div className="flex gap-xs items-center">
              <ClockCircleOutlined />
              <span className="w-full font-semibold">
                {t(`labels.${OrderHelpers.isOnSite(order) ? 'serve' : 'pickup'}`, {
                  time: formatTime(order.estimated_pickup_time),
                })}
              </span>
            </div>
            <span className="text-sm w-full text-center text-gray-600">
              {t('labels.preparation_time', { time: pickupEstimate })}
            </span>
          </TimeLabel>
          <OrderSummary order={order} />
          <OrderPaymentBreakdown order={order} />
          <OrderLineItemsList order={order} />
        </div>
        <div className="sticky bottom-0 space-y-sm">
          <Button
            type="primary"
            icon={shouldPrint ? <PrinterOutlined /> : null}
            className="uppercase w-full"
            size="large"
            loading={loading || printing}
            onClick={() => onConfirm()}
          >
            {t(`buttons.${shouldPrint ? 'print_and_confirm' : 'confirm'}`)}
          </Button>
        </div>
      </div>
    </RemoveScroll>,
    document.body,
  );
};

export default OrderPreparationReminderScreen;
